<template>
	<section v-loading="loading" class="provider-product-publish">
		<header class="publish-page-heading">
			<div>
				<div class="heading-title-row">
					<h2>{{ pageTitle }}</h2>
					<span v-if="isReadonly" class="readonly-tag">只读查看</span>
				</div>
				<p>{{ pageDescription }}</p>
			</div>
		</header>

		<!-- 步骤概览仅用于说明表单结构，发布仍在同一页面一次性完成。 -->
		<div class="publish-progress-card">
			<div v-for="(item, index) in stepList" :key="item" class="progress-item">
				<span class="progress-icon"><i :class="stepIcons[index]"></i></span>
				<div>
					<strong>{{ item }}</strong>
					<small>{{ stepDescriptions[index] }}</small>
				</div>
				<i v-if="index < stepList.length - 1" class="progress-arrow el-icon-arrow-right"></i>
			</div>
		</div>

		<el-form
			ref="goodsFormRef"
			:model="formData"
			:rules="formRules"
			label-position="right"
			label-width="126px"
			class="provider-publish-form"
			:disabled="isReadonly"
		>
			<div class="publish-form-card two-column-card">
				<div class="form-main-column">
					<div class="provider-form-title">
						<span class="title-icon"><i class="el-icon-document"></i></span>
						<div>
							<h3>基础信息</h3>
							<p>设置商品类型、标题和所属类目</p>
						</div>
					</div>
					<el-form-item label="上架类型：" prop="goodsType">
						<el-radio-group v-model="formData.goodsType">
							<el-radio :label="GOODS_TYPE.SELF">平台自营商品</el-radio>
							<el-radio :label="GOODS_TYPE.EXHIBITION">展销商品</el-radio>
						</el-radio-group>
					</el-form-item>
					<el-form-item label="商品标题：" prop="goodsTitle">
						<el-input v-model="formData.goodsTitle" maxlength="60" show-word-limit placeholder="请输入商品标题" clearable />
					</el-form-item>
					<el-form-item label="商品类目：" prop="categoryThirdId">
						<el-cascader
							v-model="categoryValue"
							:options="categoryOptions"
							:props="{ value: 'id', label: 'name', children: 'children' }"
							filterable
							clearable
							placeholder="请输入相关关键词搜索"
							class="full-control"
							@change="handleCategoryChange"
						/>
					</el-form-item>
					<el-form-item label="导购短标题：" prop="guideTitle">
						<el-input v-model="formData.guideTitle" maxlength="24" show-word-limit placeholder="请输入导购短标题" clearable />
					</el-form-item>
				</div>
				<aside class="form-help-column">
					<help-card title="商品标题">
						<li>建议 16-60 个字符，突出商品核心卖点。</li>
						<li>避免夸张、误导或与商品无关的信息。</li>
					</help-card>
					<help-card title="导购短标题">
						<li>建议 6-12 个汉字，准确表达商品亮点。</li>
						<li>与商品标题保持一致，避免堆砌关键词。</li>
					</help-card>
				</aside>
			</div>

			<div class="publish-form-card two-column-card">
				<div class="form-main-column">
					<div class="provider-form-title">
						<span class="title-icon"><i class="el-icon-s-finance"></i></span>
						<div>
							<h3>价格与履约</h3>
							<p>维护商品售价、联系方式及发货约定</p>
						</div>
					</div>
					<template v-if="isSelfGoods">
						<el-form-item label="售价：" prop="sellingPrice">
							<div class="inline-controls">
								<el-input v-model="formData.sellingPrice" placeholder="请填写价格" class="price-input" clearable />
								<el-select v-model="formData.priceUnit" class="small-select"><el-option label="元" value="元" /></el-select>
								<span class="separator">/</span>
								<el-select v-model="formData.measureUnit" class="small-select">
									<el-option v-for="item in measureUnitOptions" :key="item" :label="item" :value="item" />
								</el-select>
							</div>
						</el-form-item>
					</template>
					<template v-else>
						<el-form-item label="售价：">
							<el-radio-group v-model="formData.priceType"><el-radio :label="2">面议</el-radio></el-radio-group>
						</el-form-item>
						<el-form-item label="联系人：" prop="contactName">
							<el-input v-model="formData.contactName" placeholder="默认带入店铺联系人，支持修改" clearable />
						</el-form-item>
						<el-form-item label="联系方式：" prop="contactPhone">
							<el-input v-model="formData.contactPhone" placeholder="默认带入店铺联系方式，支持修改" clearable />
						</el-form-item>
					</template>
					<el-form-item label="发货时间：" prop="deliveryType">
						<div class="delivery-controls">
							<el-radio-group v-model="formData.deliveryType">
								<el-radio :label="1">当日发货</el-radio>
								<el-radio :label="2">24小时</el-radio>
								<el-radio :label="3">48小时</el-radio>
								<el-radio :label="4">自定义</el-radio>
							</el-radio-group>
							<el-input
								v-if="Number(formData.deliveryType) === 4"
								v-model="formData.deliveryCustom"
								placeholder="请输入发货时间"
								class="delivery-custom"
								clearable
								@input="handleDeliveryCustomInput"
							/>
						</div>
					</el-form-item>
					<el-form-item label="发货约定：">
						<el-input
							v-model="formData.deliveryAgreement"
							type="textarea"
							:rows="4"
							resize="none"
							placeholder="请输入发货、交付或服务履约说明"
						/>
					</el-form-item>
				</div>
				<aside class="form-help-column">
					<div class="business-tip">
						<i class="el-icon-info"></i>
						<strong>{{ isSelfGoods ? '自营商品计价说明' : '展销商品联系说明' }}</strong>
						<p>
							{{ isSelfGoods ? '请填写真实准确的商品售价及计量单位。' : '展销商品采用面议方式，请确保联系人及联系方式有效。' }}
						</p>
					</div>
				</aside>
			</div>

			<div class="publish-form-card">
				<div class="provider-form-title">
					<span class="title-icon"><i class="el-icon-picture-outline"></i></span>
					<div>
						<h3>图文信息</h3>
						<p>上传商品主图并编辑完整的商品详情</p>
					</div>
				</div>
				<el-form-item label="商品主图：" prop="mainImage">
					<upload-drag-images
						v-model="mainImageFiles"
						:action="uploadAction"
						:data="mainImageUploadData"
						:headers="uploadHeaders"
						:limit="10"
						:size="5"
						accept=".jpg,.jpeg,.png"
						:preview-url="previewUrl"
						:delete-api="deleteApi"
						:delete-params="mainImageUploadData"
						:disabled="isReadonly"
						@update:modelValue="handleMainImageChange"
					/>
					<p class="field-hint">最少上传 1 张、最多 10 张，建议使用 1:1 或 3:4 的清晰图片，单张不超过 5MB。</p>
				</el-form-item>
				<el-form-item label="商品详情：" prop="goodsDetail">
					<UEditor
						v-model="formData.goodsDetail"
						:height="360"
						:maximum-words="20000"
						placeholder="请编辑商品详情，支持文字格式化及插入本地图片"
						:disabled="isReadonly"
						:z-index="2"
						@change="handleRichTextChange"
					/>
				</el-form-item>
			</div>

			<div v-if="isExhibitionGoods" class="publish-form-card">
				<div class="provider-form-title">
					<span class="title-icon"><i class="el-icon-present"></i></span>
					<div>
						<h3>展销费套餐</h3>
						<p>选择套餐后，商品将获得对应时长的展销曝光权益</p>
					</div>
				</div>
				<el-form-item v-if="slotPackages.length" prop="packageId" label-width="0" class="package-form-item">
					<div class="slot-package-list" role="radiogroup" aria-label="展销费套餐">
						<div
							v-for="item in slotPackages"
							:key="getPackageKey(item)"
							class="slot-package-card"
							:class="{ active: isPackageSelected(item) }"
							role="radio"
							:aria-checked="String(isPackageSelected(item))"
							:aria-disabled="String(isReadonly)"
							:tabindex="isReadonly ? -1 : 0"
							@click="handleSelectSlotPackage(item)"
							@keydown.enter.prevent="handleSelectSlotPackage(item)"
							@keydown.space.prevent="handleSelectSlotPackage(item)"
						>
							<div class="package-card-head">
								<span>
									<i class="el-icon-present"></i>
									展销推广
								</span>
								<i class="package-check" :class="isPackageSelected(item) ? 'el-icon-check' : ''"></i>
							</div>
							<div class="package-duration">
								<strong>{{ formatDuration(item.longDuration) }}</strong>
								<small>曝光有效期</small>
							</div>
							<div class="package-price-row">
								<span class="package-price">
									<em>¥</em>
									{{ getPackagePrice(item) }}
								</span>
								<del v-if="item.packagePrice && item.packagePrice !== item.eventPrice">¥{{ item.packagePrice }}</del>
							</div>
							<div class="package-card-foot">
								<span v-if="item.tag" class="package-tag">{{ item.tag }}</span>
								<span v-else>商品审核通过后生效</span>
							</div>
						</div>
					</div>
				</el-form-item>
				<empty-content v-else empty-text="暂无展销费套餐"></empty-content>
			</div>

			<footer v-if="!isReadonly" class="publish-actions">
				<el-button :loading="draftLoading" @click="handleSaveDraft">保存草稿</el-button>
				<el-button type="primary" :loading="submitLoading" @click="handleSubmit">提交商品</el-button>
			</footer>
		</el-form>
	</section>
</template>

<script>
	import EmptyContent from '@/components/EmptyContent'
	import UEditor from '@/components/UEditor'
	import UploadDragImages from '@/components/UploadDragImages/index.vue'
	import {
		deleteMerchantFile,
		previewMerchantFile,
		queryMerchantFileList,
		queryMerchantGoodsDetail,
		queryMerchantOrderPrice,
		queryMerchantServiceCategoryWithItems,
		queryMyShop,
		saveMerchantGoodsDraft,
		submitMerchantGoods,
		uploadMerchantFile
	} from '@/api/mall'
	import { SHOP_STATE } from '@/enum'
	import { getUuid } from '@/utils'
	import { getToken } from '@/utils/auth'
	import { mapGetters } from 'vuex'

	// 商品上架类型。该值需要与旧商品接口的 goodsType 枚举保持一致。
	const GOODS_TYPE = Object.freeze({ SELF: 1, EXHIBITION: 2 })
	const createFormData = () => ({
		id: '',
		goodsType: GOODS_TYPE.EXHIBITION,
		goodsTitle: '',
		guideTitle: '',
		categoryFirstId: '',
		categoryFirstName: '',
		categorySecondId: '',
		categorySecondName: '',
		categoryThirdId: '',
		categoryThirdName: '',
		priceType: 2,
		sellingPrice: '',
		sellingPriceId: '',
		priceUnit: '元',
		measureUnit: '件',
		purchaseTime: '',
		purchaseTimeId: '',
		contactName: '',
		contactPhone: '',
		deliveryType: 1,
		deliveryCustom: '',
		deliveryAgreement: '',
		mainImage: '',
		goodsDetail: '',
		eventId: '',
		packageId: '',
		purchasePrice: '',
		// 7 表示草稿，正式提交时会在 getSubmitData 中覆盖为 1（待审核）。
		rowState: 7
	})

	export default {
		name: 'ProviderProductPublish',
		components: {
			EmptyContent,
			UEditor,
			UploadDragImages,
			// 简单提示卡使用函数式组件，避免为纯展示内容额外创建组件实例。
			HelpCard: {
				functional: true,
				props: { title: String },
				render(h, context) {
					return h('div', { class: 'goods-help-card' }, [
						h('div', { class: 'help-title' }, [h('i', { class: 'el-icon-info' }), context.props.title]),
						h('ul', context.children)
					])
				}
			}
		},
		data() {
			// 非标准表单控件及条件字段通过自定义校验器接入 Element Form 的统一校验流程。
			const validateDelivery = (rule, value, callback) => {
				if (!value) return callback(new Error('请选择发货时间'))
				if (Number(value) === 4 && !this.formData.deliveryCustom) return callback(new Error('请输入自定义发货时间'))
				callback()
			}
			const validateMainImage = (rule, value, callback) => {
				if (!this.mainImageFiles.length) return callback(new Error('请上传商品主图'))
				callback()
			}
			const validateGoodsDetail = (rule, value, callback) => {
				if (this.isRichTextEmpty(value)) return callback(new Error('请输入商品详情'))
				callback()
			}
			return {
				GOODS_TYPE,
				// 页面初始化、草稿保存和正式提交分别维护状态，避免不同操作相互覆盖加载效果。
				loading: false,
				draftLoading: false,
				submitLoading: false,
				// 店铺信息用于校验经营状态，并为展销商品带入联系人。
				shopInfo: {},
				categoryValue: [],
				// 上传组件维护完整附件对象，提交时取第一张图片作为商品封面。
				mainImageFiles: [],
				// 展销套餐列表和当前选中 ID 分开保存，便于编辑回显及提交字段映射。
				slotPackages: [],
				selectedSlotPackageId: '',
				uploadAction: uploadMerchantFile(),
				previewUrl: previewMerchantFile(),
				deleteApi: deleteMerchantFile,
				uploadHeaders: { InternetToken: getToken() },
				measureUnitOptions: ['件', '套', '项', '次', '个'],
				stepIcons: ['el-icon-document', 'el-icon-picture-outline', 'el-icon-s-finance', 'el-icon-present'],
				stepDescriptions: ['填写商品基本资料', '完善主图及详情', '设置价格和履约', '选择展销权益'],
				categoryOptions: [],
				formData: createFormData(),
				// required 同时负责必填标识，prop 将校验结果准确定位到对应表单项。
				formRules: {
					goodsType: [{ required: true, message: '请选择上架类型', trigger: 'change' }],
					goodsTitle: [{ required: true, message: '请输入商品标题', trigger: 'blur' }],
					categoryThirdId: [{ required: true, message: '请选择商品类目', trigger: 'change' }],
					guideTitle: [{ required: true, message: '请输入导购短标题', trigger: 'blur' }],
					sellingPrice: [{ required: true, message: '请填写商品售价', trigger: 'blur' }],
					contactName: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
					contactPhone: [{ required: true, message: '请输入联系方式', trigger: 'blur' }],
					deliveryType: [{ required: true, validator: validateDelivery, trigger: 'change' }],
					mainImage: [{ required: true, validator: validateMainImage, trigger: 'change' }],
					goodsDetail: [{ required: true, validator: validateGoodsDetail, trigger: ['blur', 'change'] }],
					packageId: [{ required: true, message: '请选择展销费套餐', trigger: 'change' }]
				}
			}
		},
		computed: {
			...mapGetters(['userId']),
			// URL 携带 id 时表示编辑或详情，否则为新发布。
			isEdit() {
				return Boolean(this.$route.query.id)
			},
			// readonly=1 复用同一套回显页面，但禁用全部输入和上传行为。
			isReadonly() {
				return String(this.$route.query.readonly || '') === '1'
			},
			pageTitle() {
				return this.isReadonly ? '商品详情' : this.isEdit ? '编辑商品' : '发布商品'
			},
			pageDescription() {
				return this.isReadonly ? '查看商品资料、图文信息及展销套餐' : '完善商品资料并提交平台审核'
			},
			// 商品类型计算属性集中承载模板显隐及提交参数判定，避免散落魔法数字。
			isSelfGoods() {
				return Number(this.formData.goodsType) === GOODS_TYPE.SELF
			},
			isExhibitionGoods() {
				return Number(this.formData.goodsType) === GOODS_TYPE.EXHIBITION
			},
			stepList() {
				const steps = ['基础信息', '图文信息', '价格信息']
				if (this.isExhibitionGoods) steps.push('展销权益')
				return steps
			},
			// 上传附件必须绑定稳定的业务 ID，新发布时在页面初始化阶段预生成 UUID。
			mainImageUploadData() {
				return { businessId: this.formData.id, fileBusinessType: 'GOODS_MAIN_IMAGE' }
			}
		},
		watch: {
			'formData.goodsType'(value) {
				// 切换商品类型时同步价格类型，并清理另一类型不应提交的价格字段。
				this.formData.priceType = Number(value) === GOODS_TYPE.SELF ? 1 : 2
				if (Number(value) === GOODS_TYPE.EXHIBITION) this.formData.sellingPrice = ''
				else {
					this.selectedSlotPackageId = ''
					this.formData.eventId = ''
					this.formData.packageId = ''
					this.formData.purchaseTime = ''
					this.formData.purchaseTimeId = ''
					this.formData.purchasePrice = ''
					this.formData.sellingPriceId = ''
				}
			}
		},
		created() {
			this.formData.id = this.$route.query.id || getUuid()
			this.initPage()
		},
		methods: {
			// 初始化顺序不可调换：店铺校验和套餐先准备，再通过详情覆盖默认表单值。
			async initPage() {
				this.loading = true
				try {
					const canPublish = await this.loadShop()
					if (!canPublish) return
					await this.loadCategoryOptions()
					await this.loadSlotPackages()
					if (this.isEdit) await this.loadGoodsDetail(this.$route.query.id)
				} finally {
					this.loading = false
				}
			},
			async loadCategoryOptions() {
				const response = await queryMerchantServiceCategoryWithItems()
				const rows = Array.isArray(response.data) ? response.data : []
				const categoryMap = new Map()
				rows.forEach(row => {
					if (!categoryMap.has(String(row.categoryId))) {
						categoryMap.set(String(row.categoryId), {
							id: row.categoryId,
							categoryName: row.categoryName,
							parentId: row.parentId,
							sortNo: row.categorySortNo
						})
					}
				})
				const categories = Array.from(categoryMap.values())
				const items = rows
					.filter(row => row.itemId)
					.map(row => ({
						id: row.itemId,
						categoryId: row.categoryId,
						itemName: row.itemName,
						sortNo: row.itemSortNo
					}))
				this.categoryOptions = this.buildCategoryTree(categories, items)
			},
			buildCategoryTree(categories, items) {
				const categoryNodes = new Map()
				const sortedCategories = [...categories].sort((a, b) => Number(a.sortNo || 0) - Number(b.sortNo || 0))
				sortedCategories.forEach(category => {
					categoryNodes.set(String(category.id), {
						id: category.id,
						name: category.categoryName,
						sortNo: category.sortNo,
						children: []
					})
				})
				const roots = []
				sortedCategories.forEach(category => {
					const node = categoryNodes.get(String(category.id))
					const parent = categoryNodes.get(String(category.parentId))
					if (parent && String(category.parentId) !== '0') parent.children.push(node)
					else roots.push(node)
				})
				const sortedItems = [...items].sort((a, b) => Number(a.sortNo || 0) - Number(b.sortNo || 0))
				sortedItems.forEach(item => {
					const category = categoryNodes.get(String(item.categoryId))
					if (category) category.children.push({ id: item.id, name: item.itemName })
				})
				return roots
			},
			// 只有正常经营的店铺可以发布；此业务判定沿用旧发布页面。
			async loadShop() {
				const response = await queryMyShop().catch(() => ({ data: null }))
				this.shopInfo = response.data || {}
				if (!this.shopInfo.id || Number(this.shopInfo.shopState) !== SHOP_STATE.NORMAL) {
					this.$message.warning('只有正常经营的店铺才能发布商品')
					this.$router.replace('/provider/store')
					return false
				}
				this.formData.contactName = this.formData.contactName || this.shopInfo.contactPerson || this.shopInfo.contactName || ''
				this.formData.contactPhone = this.formData.contactPhone || this.shopInfo.contactPhone || this.shopInfo.telephone || ''
				return true
			},
			// 套餐接口可能返回数组或按类型分组的对象，这里统一展开为列表。
			async loadSlotPackages() {
				const response = await queryMerchantOrderPrice({ packageTypeList: 500, userId: this.userId }).catch(() => ({ data: [] }))
				const list = Array.isArray(response.data)
					? response.data
					: Object.keys(response.data || {}).reduce((result, key) => result.concat(response.data[key] || []), [])
				this.slotPackages = list.sort((a, b) => Number(b.longDuration || 0) - Number(a.longDuration || 0))
			},
			// 编辑与查看共用详情回显，并兼容接口历史上的多种数据包裹结构。
			async loadGoodsDetail(id) {
				const response = await queryMerchantGoodsDetail({ id })
				const goods = response.data || {}
				const detail = goods.merchantGoods || goods.goods || goods
				this.formData = {
					...this.formData,
					...detail,
					id: detail.id || id,
					goodsType: Number(detail.goodsType || this.formData.goodsType),
					deliveryType: Number(detail.deliveryType || 1),
					priceType: Number(detail.priceType || (Number(detail.goodsType) === GOODS_TYPE.SELF ? 1 : 2))
				}
				this.categoryValue = [detail.categoryFirstId, detail.categorySecondId, detail.categoryThirdId].filter(Boolean)
				this.selectedSlotPackageId = detail.packageId || detail.purchaseTimeId || detail.sellingPriceId || ''
				this.mainImageFiles = this.normalizeFiles(goods.merchantFiles || goods.fileList || [])
				if (!this.mainImageFiles.length) await this.loadFiles(this.formData.id)
			},
			// 详情未直接返回附件时，再按商品业务 ID 补查主图列表。
			async loadFiles(businessId) {
				const response = await queryMerchantFileList({ businessId }).catch(() => ({ data: [] }))
				this.mainImageFiles = this.normalizeFiles(response.data).filter(item => item.fileBusinessType === 'GOODS_MAIN_IMAGE')
			},
			// 将附件接口结构转换成上传组件可识别的 fileId/url 结构。
			normalizeFiles(files) {
				if (!Array.isArray(files)) return []
				return files.map(item => {
					const fileId = item.id || item.fileId || item.fileRecordId
					return { fileId, fileBusinessType: item.fileBusinessType, url: fileId ? previewMerchantFile() + fileId : item.url }
				})
			},
			// 级联选择结果需要拆分为三级 ID 和名称，以匹配旧商品提交接口字段。
			handleCategoryChange(value) {
				const nodes = this.findCategoryNodes(value)
				const first = nodes[0]
				const second = nodes.length > 2 ? nodes[1] : null
				const third = nodes.length > 1 ? nodes[nodes.length - 1] : null
				this.setCategoryLevel('First', first)
				this.setCategoryLevel('Second', second)
				this.setCategoryLevel('Third', third)
				this.$nextTick(() => this.$refs.goodsFormRef && this.$refs.goodsFormRef.validateField('categoryThirdId'))
			},
			setCategoryLevel(level, node) {
				this.formData[`category${level}Id`] = node ? node.id : ''
				this.formData[`category${level}Name`] = node ? node.name : ''
			},
			// 上传组件的值不直接属于 formData，需要同步封面字段并主动刷新该表单项校验状态。
			handleMainImageChange(files) {
				this.mainImageFiles = Array.isArray(files) ? files : []
				const mainFile = this.mainImageFiles[0] || {}
				this.formData.mainImage = mainFile.fileId || mainFile.id || ''
				this.$nextTick(() => this.$refs.goodsFormRef && this.$refs.goodsFormRef.validateField('mainImage'))
			},
			// 自定义发货时间与 deliveryType 共用一个校验项，录入时及时清除原错误提示。
			handleDeliveryCustomInput() {
				this.$nextTick(() => this.$refs.goodsFormRef && this.$refs.goodsFormRef.validateField('deliveryType'))
			},
			// 富文本组件不是 Element 原生表单控件，内容变化后主动触发对应字段校验。
			handleRichTextChange() {
				this.$nextTick(() => this.$refs.goodsFormRef && this.$refs.goodsFormRef.validateField('goodsDetail'))
			},
			// 根据级联 ID 路径逐层查找完整节点，用于同时保存类目名称。
			findCategoryNodes(value) {
				if (!Array.isArray(value)) return []
				const nodes = []
				let options = this.categoryOptions
				value.forEach(id => {
					const node = options.find(item => item.id === id)
					if (node) {
						nodes.push(node)
						options = node.children || []
					}
				})
				return nodes
			},
			getPackageKey(item) {
				return item.itemPackageId || item.id || item.itemEventPromotionId
			},
			// 接口历史数据中的套餐 ID 可能为数字或字符串，统一转字符串判断选中状态。
			isPackageSelected(item) {
				return String(this.selectedSlotPackageId) === String(this.getPackageKey(item))
			},
			getPackagePrice(item) {
				if (!item) return ''
				if (item.eventPrice !== undefined && item.eventPrice !== null && item.eventPrice !== '') return item.eventPrice
				if (item.packagePrice !== undefined && item.packagePrice !== null && item.packagePrice !== '') return item.packagePrice
				return 0
			},
			getSelectedPackagePrice() {
				const selected = this.slotPackages.find(item => String(this.getPackageKey(item)) === String(this.selectedSlotPackageId))
				return this.getPackagePrice(selected)
			},
			// 选择套餐时同时填充旧提交接口要求的活动、套餐、时长及价格字段。
			handleSelectSlotPackage(item) {
				if (this.isReadonly) return
				const packageId = this.getPackageKey(item)
				this.selectedSlotPackageId = packageId
				this.formData.eventId = item.itemEventPromotionId
				this.formData.packageId = item.itemPackageId || packageId
				this.formData.purchaseTime = item.longDuration
				this.formData.purchaseTimeId = item.itemPackageId || packageId
				this.formData.sellingPriceId = item.id || item.itemPackageId
				this.formData.purchasePrice = this.getPackagePrice(item)
				this.$nextTick(() => this.$refs.goodsFormRef && this.$refs.goodsFormRef.validateField('packageId'))
			},
			formatDuration(days) {
				const value = Number(days || 0)
				if (!value) return '按套餐时长'
				if (value >= 365) return `${Math.floor(value / 365)}年`
				if (value >= 30) return `${Math.ceil(value / 30)}个月`
				return `${value}天`
			},
			// 过滤富文本生成的空标签、空格和占位实体，避免视觉为空的内容通过必填校验。
			isRichTextEmpty(content) {
				return !String(content || '')
					.replace(/<[^>]+>/g, '')
					.replace(/&nbsp;|\s/g, '')
			},
			// 按旧接口参数组装提交载荷，主图取上传列表第一张，rowState 区分草稿和审核提交。
			getSubmitData(rowState) {
				const mainFile = this.mainImageFiles[0] || {}
				const data = {
					...this.formData,
					priceType: this.isExhibitionGoods ? 2 : 1,
					mainImage: mainFile.fileId || this.formData.mainImage || '',
					purchasePrice: this.isExhibitionGoods
						? this.formData.purchasePrice !== undefined &&
						  this.formData.purchasePrice !== null &&
						  this.formData.purchasePrice !== ''
							? this.formData.purchasePrice
							: this.getSelectedPackagePrice()
						: '',
					rowState
				}
				if (this.isSelfGoods) {
					Object.assign(data, {
						eventId: '',
						packageId: '',
						purchaseTime: '',
						purchaseTimeId: '',
						purchasePrice: '',
						sellingPriceId: ''
					})
				}
				return data
			},
			async handleSaveDraft() {
				this.draftLoading = true
				try {
					await saveMerchantGoodsDraft(this.getSubmitData(7))
					this.$message.success('草稿已保存')
					this.redirectToProductList()
				} finally {
					this.draftLoading = false
				}
			},
			async handleSubmit() {
				// 正式提交统一执行 rules 校验；草稿保存仍允许字段暂未填写完整。
				const valid = await new Promise(resolve => this.$refs.goodsFormRef.validate(resolve))
				if (!valid) return
				this.submitLoading = true
				try {
					await submitMerchantGoods(this.getSubmitData(1))
					this.$message.success('商品已提交，请等待审核')
					this.redirectToProductList()
				} finally {
					this.submitLoading = false
				}
			},
			// 保存或提交成功后统一回到新服务商端商品管理页。
			redirectToProductList() {
				this.$router.push('/provider/products')
			}
		}
	}
</script>

<style lang="scss" scoped>
	.provider-product-publish {
		min-height: 100%;
		padding: 24px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #f7f9fc;
		box-sizing: border-box;
	}
	.publish-page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 0 20px;
		border-bottom: 1px solid #e3e9f0;
		.heading-title-row {
			display: flex;
			align-items: center;
		}
		h2 {
			margin: 0;
			color: #303846;
			font-size: 21px;
		}
		p {
			margin: 7px 0 0;
			color: #8e98a6;
			font-size: 13px;
		}
	}
	.readonly-tag {
		margin-left: 10px;
		padding: 3px 9px;
		border-radius: 11px;
		color: #687486;
		font-size: 12px;
		background: #e9edf2;
	}
	.publish-progress-card {
		display: flex;
		align-items: center;
		margin: 18px 0;
		padding: 18px 22px;
		border: 1px solid #e1e9f2;
		border-radius: 8px;
		background: #fff;
	}
	.progress-item {
		position: relative;
		display: flex;
		flex: 1;
		align-items: center;
		min-width: 0;
		.progress-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 42px;
			height: 42px;
			margin-right: 11px;
			border-radius: 50%;
			color: #fff;
			font-size: 18px;
			background: linear-gradient(145deg, #3c8be1, #2671cb);
			box-shadow: 0 5px 12px rgba(38, 113, 203, 0.2);
		}
		strong,
		small {
			display: block;
		}
		strong {
			color: #344154;
			font-size: 14px;
		}
		small {
			margin-top: 4px;
			color: #9aa3af;
			font-size: 11px;
		}
		.progress-arrow {
			margin: 0 20px 0 auto;
			color: #bcc9d7;
		}
	}
	.publish-form-card {
		margin-bottom: 16px;
		padding: 22px 26px 8px;
		border: 1px solid #e3eaf2;
		border-radius: 8px;
		background: #fff;
	}
	.two-column-card {
		display: flex;
		gap: 26px;
	}
	.form-main-column {
		flex: 1;
		min-width: 0;
	}
	.form-help-column {
		flex: 0 0 230px;
		padding-top: 58px;
	}
	.provider-form-title {
		display: flex;
		align-items: center;
		margin-bottom: 20px;
		padding-bottom: 15px;
		border-bottom: 1px solid #edf1f5;
		.title-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 36px;
			height: 36px;
			margin-right: 11px;
			border-radius: 8px;
			color: #2671cb;
			font-size: 18px;
			background: #eaf3fd;
		}
		h3 {
			margin: 0;
			color: #303b4b;
			font-size: 17px;
		}
		p {
			margin: 4px 0 0;
			color: #9aa3ae;
			font-size: 12px;
		}
	}
	.provider-publish-form {
		::v-deep .el-form-item {
			margin-bottom: 17px;
		}
		::v-deep .el-form-item__label {
			padding-right: 14px;
			color: #465365;
			font-weight: 500;
			line-height: 38px;
		}
		::v-deep .el-input__inner {
			height: 38px;
			line-height: 38px;
		}
		::v-deep .el-input__count {
			line-height: 36px;
		}
		.full-control {
			width: 100%;
		}
	}
	.goods-help-card,
	.business-tip {
		margin-bottom: 14px;
		padding: 14px;
		border: 1px solid #dfe9f4;
		border-radius: 7px;
		color: #667384;
		font-size: 12px;
		line-height: 20px;
		background: #f7faff;
		.help-title {
			margin-bottom: 8px;
			color: #40516a;
			font-size: 13px;
			font-weight: 600;
			i {
				margin-right: 6px;
				color: #3481d5;
			}
		}
		ul {
			margin: 0;
			padding-left: 17px;
		}
	}
	.business-tip {
		> i {
			margin-right: 6px;
			color: #3481d5;
		}
		strong {
			color: #40516a;
		}
		p {
			margin: 7px 0 0;
		}
	}
	.inline-controls,
	.delivery-controls {
		display: flex;
		align-items: center;
		gap: 10px;
	}
	.delivery-controls {
		min-height: 38px;

		::v-deep .el-radio-group {
			display: flex;
			align-items: center;
			height: 38px;
			line-height: normal;
		}

		::v-deep .el-radio {
			display: inline-flex;
			align-items: center;
		}
	}
	.price-input {
		flex: 1;
		min-width: 160px;
	}
	.small-select {
		width: 88px;
	}
	.separator {
		color: #8691a0;
	}
	.delivery-custom {
		width: 180px;
	}
	.field-hint {
		margin: 8px 0 0;
		color: #98a1ad;
		font-size: 12px;
		line-height: 18px;
	}
	.slot-package-list {
		display: grid;
		grid-template-columns: repeat(3, minmax(190px, 1fr));
		gap: 16px;
		padding: 2px 0 10px;
	}
	.package-form-item {
		margin-bottom: 8px !important;
	}
	.slot-package-card {
		position: relative;
		display: flex;
		flex-direction: column;
		min-height: 184px;
		padding: 17px 18px 14px;
		overflow: hidden;
		border: 1px solid #dfe7f0;
		border-radius: 10px;
		color: #465365;
		outline: none;
		background: linear-gradient(145deg, #ffffff 0%, #f7faff 100%);
		cursor: pointer;
		transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

		&:hover:not([aria-disabled='true']),
		&:focus:not([aria-disabled='true']) {
			border-color: #8bb9e8;
			box-shadow: 0 8px 22px rgba(38, 113, 203, 0.12);
			transform: translateY(-2px);
		}
		&.active {
			border-color: #2671cb;
			background: linear-gradient(145deg, #ffffff 0%, #edf6ff 100%);
			box-shadow: 0 8px 24px rgba(38, 113, 203, 0.16), 0 0 0 1px #2671cb inset;
		}
		&[aria-disabled='true'] {
			cursor: default;
			opacity: 0.82;
		}
	}
	.package-card-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		color: #718095;
		font-size: 12px;

		span i {
			margin-right: 5px;
			color: #4b8dd4;
		}
	}
	.package-check {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 18px;
		height: 18px;
		border: 1px solid #cbd6e2;
		border-radius: 50%;
		color: #ffffff;
		font-size: 11px;
		background: #ffffff;

		.active & {
			border-color: #2671cb;
			background: #2671cb;
		}
	}
	.package-duration {
		display: flex;
		align-items: baseline;
		margin-top: 18px;

		strong {
			color: #2f3e52;
			font-size: 25px;
			font-weight: 600;
		}
		small {
			margin-left: 8px;
			color: #9aa5b2;
			font-size: 11px;
		}
	}
	.package-price-row {
		display: flex;
		align-items: baseline;
		margin-top: 10px;

		del {
			margin-left: 9px;
			color: #aeb7c2;
			font-size: 12px;
		}
	}
	.package-price {
		color: #e16c32;
		font-size: 24px;
		font-weight: 700;

		em {
			margin-right: 2px;
			font-size: 13px;
			font-style: normal;
		}
	}
	.package-card-foot {
		margin-top: auto;
		padding-top: 11px;
		border-top: 1px dashed #dce5ee;
		color: #8a96a5;
		font-size: 11px;
	}
	.package-tag {
		display: inline-block;
		padding: 2px 7px;
		border-radius: 9px;
		color: #2671cb;
		background: #e4f0fd;
	}
	.publish-actions {
		position: sticky;
		bottom: 0;
		z-index: 3;
		display: flex;
		justify-content: center;
		gap: 16px;
		padding: 16px;
		border: 1px solid #e1e8f0;
		border-radius: 8px;
		background: rgba(255, 255, 255, 0.96);
		box-shadow: 0 -5px 18px rgba(40, 64, 92, 0.06);
		.el-button {
			min-width: 128px;
			margin-left: 0;
		}
	}
	@media (max-width: 1360px) {
		.form-help-column {
			flex-basis: 205px;
		}
		.slot-package-list {
			grid-template-columns: repeat(2, minmax(190px, 1fr));
		}
		.progress-item .progress-arrow {
			margin-right: 10px;
		}
	}
</style>
